import { type SxProps, TableBody, TableContainer, TableHead } from '@mui/material'
import TableMU from '@mui/material/Table'
import {
  type ReactNode,
  useEffect,
  useState,
  useRef,
  type CSSProperties,
  type UIEvent,
} from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import {
  type IColumn,
  type IRow,
  type IRowDefault,
  type IRowExtractType,
} from 'shared/ui/TableNew/store/types'
import { type IScrollbarProps, Scrollbar } from 'shared/ui/Scrollbar'
import { SpinnerLoader } from 'shared/ui/Loader'
import { TableRowHead } from 'shared/ui/TableNew/ui/TableRowHead/TableRowHead'
import { type TableStore } from 'shared/ui/TableNew/store/tableStore'
import { Pagination } from 'shared/ui/TableNew/ui/Pagination/Pagination'
import { EmptyState, type IDefaultEmptyStateProps, Box } from 'shared/ui'
import { ExpandedTableRow } from 'shared/ui/TableNew/ui/TableRow/ExpandedTableRow'
import { type IHeaderLeftContentProps } from 'shared/ui/TableNew/ui/TableRowHead/HeaderLeftContent/HeaderLeftContent'
import { HeaderSelectedContent } from 'shared/ui/TableNew/ui/TableRowHead/HeaderSelectedContent/HeaderSelectedContent'
import styles from './Table.module.scss'

export type ITableProps<TRow extends IRowDefault> = {
  store: TableStore<TRow>

  rows: IRow<TRow>[]
  loading?: boolean
  hidePagination?: boolean

  activeRowId?: TRow['id']
  inActionId?: number | null
  onRowClick?: (row: TRow) => void
  onRowChildClick?: (row: IRowExtractType<TRow>) => void

  getRowLink?: (row: TRow) => string
  getRowChildLink?: (row: IRowExtractType<TRow>) => string

  columns: IColumn<TRow>[]
  columnsChild?: IColumn<IRowExtractType<TRow>>[]

  hideExpandColumnsIds?: string[]
  pinnedColumnIds?: string[]
  noMinWidthColumns?: boolean

  withCheckbox?: boolean
  withSelectAll?: boolean
  withoutSelectedCount?: boolean

  defaultWidthColumn?: CSSProperties['width']
  defaultMinWidthColumn?: CSSProperties['minWidth']
  defaultMaxWidthColumn?: CSSProperties['maxWidth']
  defaultColumnCellSx?: SxProps
  sxTable?: SxProps
  sxBox?: SxProps
  height?: string

  rightHeaderContent?: ReactNode
  actionsCenter?: ReactNode
  bottomTableContent?: ReactNode

  onChangePagination?: (page: number, pageSize: number) => void

  scrollbarProps?: Partial<Omit<IScrollbarProps, 'ref'>>
  emptyStateProps?: IDefaultEmptyStateProps

  isCheckDate?: boolean

  scrollToTOpTrigger?: string
  isHoveredRowActions?: boolean
  isCursor?: boolean
  headerLeftContentProps?: IHeaderLeftContentProps
  ariaLabel: string
}

const TableContent = observer(
  <TRow extends IRowDefault>({
    store,
    columns,
    columnsChild,
    rows,
    activeRowId,
    rightHeaderContent,
    pinnedColumnIds = [],
    inActionId,
    withCheckbox,
    withSelectAll,
    withoutSelectedCount,
    hideExpandColumnsIds,
    hidePagination,
    isCheckDate,
    defaultWidthColumn,
    defaultMinWidthColumn,
    defaultMaxWidthColumn,
    defaultColumnCellSx,
    height = '100%',
    onRowClick,
    onRowChildClick,
    getRowLink,
    getRowChildLink,
    sxTable,
    sxBox,
    bottomTableContent,
    scrollToTOpTrigger,
    isHoveredRowActions,
    isCursor,
    headerLeftContentProps,
    scrollbarProps,
    ariaLabel,
    ...props
  }: ITableProps<TRow>) => {
    const ref = useRef(null)
    const [isScrollLeft, setIsScrollLeft] = useState(false)
    const { columnsStore, bulkStore } = store
    const withActions = rows.some((item) => item.withActions)
    const loading = props.loading || store.loading
    const selected = store.bulkStore.isSelected || withoutSelectedCount

    const onChangePagination = (page: number, limit: number) => {
      store.setPage(page)
      store.setLimit(limit)
      props.onChangePagination?.(page, limit)
    }

    const onScroll = (e: UIEvent) => {
      const target = e.target as HTMLDivElement
      setIsScrollLeft(!!target.scrollLeft)
    }

    const isSelected = (row: TRow) => bulkStore.selectedIds.includes(row.id)

    useEffect(() => {
      store.setRows(rows)

      if (!store.total) store.setTotal(rows.length)
    }, [rows])

    useEffect(() => {
      columnsStore.initVisibleColumnsIds()
      columnsStore.setPinnedColumnIds(pinnedColumnIds)
    }, [])

    useEffect(() => {
      rows.forEach((row) => {
        row.setColumnsKeys(columnsStore.visibleColumnsIds)
      })
    }, [columnsStore.visibleColumnsIds])

    useEffect(() => {
      columnsStore.setColumns(columns)
    }, [columns])

    useEffect(() => {
      columnsStore.setColumnsChild(columnsChild)
    }, [columnsChild])

    if (!store.total && !loading) {
      return <EmptyState {...props.emptyStateProps} />
    }

    return (
      <Box
        sx={{
          flex: '1 1 auto',
          ...sxBox,
        }}
        height={height}
        width='100%'
        display='flex'
        flexDirection='column'
        position='relative'
        ref={ref}
      >
        {loading ? (
          <SpinnerLoader ariaLabel={`Table_${ariaLabel}`} />
        ) : (
          <>
            <TableContainer style={{ flex: 1 }}>
              <Scrollbar
                scrollToTOpTrigger={scrollToTOpTrigger}
                viewClassName={styles.viewScrollClassName}
                {...scrollbarProps}
                onScroll={(event) => {
                  onScroll(event)
                  scrollbarProps?.onScroll?.(event)
                }}
              >
                <HeaderSelectedContent
                  store={store}
                  headerLeftContentProps={headerLeftContentProps}
                  withoutSelectedCount={Boolean(withoutSelectedCount)}
                  rightHeaderContent={rightHeaderContent}
                  withActions={withActions}
                  withSelectAll={withSelectAll}
                />

                <TableMU sx={sxTable}>
                  <TableHead
                    className={classNames(styles.tableHead, {
                      [styles['tableHead--hide']]: selected,
                    })}
                  >
                    <TableRowHead
                      store={store}
                      selected={bulkStore.isSelected}
                      withCheckbox={withCheckbox}
                      withActions={withActions}
                      rightHeaderContent={rightHeaderContent}
                      isScrollLeft={isScrollLeft}
                      onSort={store.onSort}
                      sortBy={store.sortBy}
                      sortOrder={store.sortOrder}
                    />
                  </TableHead>

                  <TableBody aria-label='TableBody'>
                    {rows.map((row) => (
                      <ExpandedTableRow
                        store={store}
                        key={row.id}
                        row={row}
                        selected={isSelected(row)}
                        inAction={inActionId === row.id}
                        withCheckbox={withCheckbox}
                        hideExpandColumnsIds={hideExpandColumnsIds}
                        onRowSelect={store.onRowSelect}
                        onRowClick={onRowClick}
                        onRowChildClick={onRowChildClick}
                        activeRowId={activeRowId}
                        isCheckDate={isCheckDate}
                        getRowLink={getRowLink}
                        getRowChildLink={getRowChildLink}
                        defaultWidthColumn={defaultWidthColumn}
                        defaultMaxWidthColumn={defaultMaxWidthColumn}
                        defaultMinWidthColumn={defaultMinWidthColumn}
                        defaultColumnCellSx={defaultColumnCellSx}
                        isScrollLeft={isScrollLeft}
                        isHoveredRowActions={isHoveredRowActions}
                        isCursor={isCursor}
                        refTable={ref}
                      />
                    ))}
                    {bottomTableContent}
                  </TableBody>
                </TableMU>
              </Scrollbar>
            </TableContainer>

            {!hidePagination && (
              <Pagination
                total={store.total}
                limit={store.limit}
                initPage={store.page}
                onChange={onChangePagination}
                startSize={store.startLimit}
              />
            )}
          </>
        )}
      </Box>
    )
  }
)

export const Table = observer(
  <TRow extends IRowDefault>({ store, ...props }: ITableProps<TRow>) => {
    return <TableContent store={store} {...props} />
  }
)
